/**
 * QueueIdentityFields — client + optional brand identity fields for the
 * `QueueJoinPanel` seam (CU-86cb7ewja). Extracted from `queue-join-panel.tsx`
 * (max-lines split) — shared by `QueueFirstJoinBlock` and `QueueJoinPanel`'s
 * own inline "add another client" form.
 */
import { LabeledSearchableSelectField } from '@ooh/ui/components/labeled-searchable-select-field';
import type {
  QueueJoinClientOption,
  QueueJoinOfferOption,
  QueueJoinPanelLabels,
} from './queue-join-types';

/** Client + optional brand identity fields — brand options narrow to the selected client's own brands. */
export function QueueIdentityFields({
  testId,
  clientOptions,
  clientId,
  brand,
  offerId,
  onClientChange,
  onBrandChange,
  onOfferChange,
  offerOptions,
  labels,
}: {
  testId: string;
  clientOptions: QueueJoinClientOption[];
  clientId: string;
  brand: string;
  offerId: string;
  onClientChange: (value: string) => void;
  onBrandChange: (value: string) => void;
  onOfferChange: (value: string) => void;
  offerOptions: QueueJoinOfferOption[];
  labels: QueueJoinPanelLabels;
}) {
  const selectedClient = clientOptions.find((option) => option.value === clientId);
  const brandOptions = (selectedClient?.brands ?? []).map((value) => ({ value, label: value }));
  const filteredOfferOptions = offerOptions.filter(
    (option) =>
      option.clientId === Number(clientId) &&
      (!brand || option.brand === null || option.brand === brand),
  );
  const validationLabel =
    selectedClient && selectedClient.brands.length === 0 ? labels.noBrandsLabel : null;

  return (
    <div className="grid grid-cols-2 gap-2">
      <LabeledSearchableSelectField
        id={`${testId}-client`}
        label={labels.clientLabel}
        options={clientOptions}
        value={clientId}
        onChange={(value) => {
          onClientChange(value);
          onBrandChange('');
          onOfferChange('');
        }}
        placeholder={labels.clientPlaceholder}
        searchPlaceholder={labels.clientSearchPlaceholder}
        testId={`${testId}-client-select`}
      />
      <LabeledSearchableSelectField
        id={`${testId}-brand`}
        label={labels.brandLabel}
        options={brandOptions}
        value={brand}
        onChange={(value) => {
          onBrandChange(value);
          onOfferChange('');
        }}
        placeholder={labels.brandPlaceholder}
        searchPlaceholder={labels.brandSearchPlaceholder}
        testId={`${testId}-brand-select`}
      />
      {clientId ? (
        <div className="col-span-2">
          <LabeledSearchableSelectField
            id={`${testId}-offer`}
            label={labels.offerLabel}
            options={filteredOfferOptions}
            value={offerId}
            onChange={onOfferChange}
            placeholder={labels.offerPlaceholder}
            searchPlaceholder={labels.offerSearchPlaceholder}
            testId={`${testId}-offer-select`}
          />
        </div>
      ) : null}
      {validationLabel ? (
        <p className="col-span-2 text-xs text-destructive" role="alert">
          {validationLabel}
        </p>
      ) : null}
    </div>
  );
}
